import { flexRender, Row, RowData, Table as TanStackTable } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Alert, Group, Loader, Table, Text } from '@common/ui'
import { Fragment, ReactNode, useEffect, useRef } from 'react'
import classes from './InfiniteScrollTable.module.css'

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    // Let a column wrap its content onto multiple lines instead of truncating.
    wrap?: boolean
  }
}

type InfiniteScrollTableProps<T> = {
  table: TanStackTable<T>
  loading: boolean
  hasMore: boolean
  loadMore: () => void
  onRowClick?: (row: Row<T>) => void
  emptyMessage?: string
  estimateRowSize?: number
  containerTestId?: string
  // Virtualize rows (default). Set false for short lists with variable-height
  // rows (e.g. expandable detail rows), which scroll with the page instead.
  virtualized?: boolean
  // Optional expandable content rendered below each row (non-virtualized only).
  // Return null for rows without detail.
  renderDetail?: (row: Row<T>) => ReactNode
  error?: Error | null
}

const headerRowStyle = {
  display: 'flex',
  backgroundColor: 'var(--trellis-color-surface)',
  minWidth: '100%',
} as const

const rowFlexStyle = { display: 'flex', width: '100%' } as const

const cellInnerStyle = (wrap?: boolean): React.CSSProperties =>
  wrap
    ? { flex: 1, minWidth: 0, whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere' }
    : { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

const InfiniteScrollTable = <T,>({
  table,
  loading,
  hasMore,
  loadMore,
  onRowClick,
  emptyMessage = 'No results',
  estimateRowSize = 45,
  containerTestId,
  virtualized = true,
  renderDetail,
  error,
}: InfiniteScrollTableProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => estimateRowSize,
    overscan: 5,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const lastVirtualRowIndex = virtualRows[virtualRows.length - 1]?.index ?? -1

  useEffect(() => {
    if (!virtualized) return
    if (lastVirtualRowIndex >= rows.length - 1 && hasMore && !loading) loadMore()
  }, [virtualized, hasMore, loading, rows.length, lastVirtualRowIndex, loadMore])

  useEffect(() => {
    if (virtualized) return
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (intersection) => {
        if (intersection[0]?.isIntersecting && hasMore && !loading) loadMore()
      },
      { rootMargin: '256px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [virtualized, hasMore, loading, loadMore])

  if (error) return <Alert severity="negative">{error.message}</Alert>

  const columnCount = table.getVisibleFlatColumns().length

  const renderHeader = () =>
    table.getHeaderGroups().map((headerGroup) => (
      <Table.Tr key={headerGroup.id} style={headerRowStyle}>
        {headerGroup.headers.map((header, index) => (
          <Table.Th
            key={header.id}
            style={{
              width: header.getSize(),
              minWidth: header.column.columnDef.minSize ?? header.getSize(),
              flexGrow: index === headerGroup.headers.length - 1 ? 1 : 0,
              position: 'relative',
              backgroundColor: 'inherit',
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
              borderBottom: '1px solid var(--trellis-color-outline)',
            }}
          >
            {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
            {header.column.getCanResize() && (
              <button
                type="button"
                aria-hidden="true"
                tabIndex={-1}
                onMouseDown={header.getResizeHandler()}
                onTouchStart={header.getResizeHandler()}
                className={classes.resizeHandle}
                data-resizing={header.column.getIsResizing() ? 'true' : undefined}
              />
            )}
          </Table.Th>
        ))}
      </Table.Tr>
    ))

  const renderCells = (row: Row<T>) => {
    const cells = row.getVisibleCells()
    return cells.map((cell, index) => (
      <Table.Td
        key={cell.id}
        style={{
          width: cell.column.getSize(),
          flexGrow: index === cells.length - 1 ? 1 : 0,
          minWidth: 0,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div style={cellInnerStyle(cell.column.columnDef.meta?.wrap)}>
          {flexRender(cell.column.columnDef.cell, cell.getContext())}
        </div>
      </Table.Td>
    ))
  }

  const emptyRow = (
    <Table.Tr style={rowFlexStyle}>
      <Table.Td colSpan={columnCount} style={{ flex: 1, textAlign: 'center', border: 'none' }}>
        <Text size="sm" c="dimmed" py="xl">
          {emptyMessage}
        </Text>
      </Table.Td>
    </Table.Tr>
  )

  const renderVirtualRows = () =>
    rows.length === 0 && !loading
      ? emptyRow
      : virtualRows.map((virtualRow) => {
          const row = rows[virtualRow.index]
          return (
            <Table.Tr
              key={row.id}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                cursor: onRowClick ? 'pointer' : 'default',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
                display: 'flex',
              }}
            >
              {renderCells(row)}
            </Table.Tr>
          )
        })

  const renderPlainRows = () => {
    if (rows.length === 0 && !loading) return emptyRow
    return rows.map((row) => {
      const detail = renderDetail?.(row)
      return (
        <Fragment key={row.id}>
          <Table.Tr
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            style={{ ...rowFlexStyle, cursor: onRowClick ? 'pointer' : 'default' }}
          >
            {renderCells(row)}
          </Table.Tr>
          {detail && (
            <Table.Tr style={rowFlexStyle}>
              <Table.Td style={{ flex: 1, paddingTop: 0, paddingBottom: 0, border: 'none' }}>{detail}</Table.Td>
            </Table.Tr>
          )}
        </Fragment>
      )
    })
  }

  return (
    <div
      ref={containerRef}
      data-testid={containerTestId}
      style={virtualized ? { flex: 1, overflow: 'auto' } : { width: '100%', overflowX: 'auto' }}
    >
      <Table
        verticalSpacing="xs"
        style={{
          tableLayout: 'fixed',
          minWidth: 'max-content',
          display: 'grid',
          width: virtualized ? undefined : '100%',
        }}
      >
        <Table.Thead
          style={virtualized ? { position: 'sticky', top: 0, zIndex: 1, display: 'grid' } : { display: 'grid' }}
        >
          {renderHeader()}
        </Table.Thead>
        <Table.Tbody
          style={
            virtualized
              ? { height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative', display: 'grid' }
              : { display: 'grid' }
          }
        >
          {virtualized ? renderVirtualRows() : renderPlainRows()}
        </Table.Tbody>
      </Table>
      {virtualized ? (
        loading && (
          <Group
            gap="xs"
            justify="center"
            py="md"
            style={{
              position: 'sticky',
              left: 0,
              width: containerRef.current?.clientWidth ?? '100%',
            }}
          >
            <Loader size="xs" />
            <Text size="sm" c="dimmed">
              Loading…
            </Text>
          </Group>
        )
      ) : (
        <div ref={sentinelRef} style={{ display: 'flex', justifyContent: 'center', paddingBlock: 16 }}>
          {loading && <Loader size="sm" />}
        </div>
      )}
    </div>
  )
}

export default InfiniteScrollTable
